import { createFileRoute } from '@tanstack/react-router';
import { formatDateNumeric } from '@/lib/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  type DiffRow,
  type DriftRow,
  type EntityType,
  importSyncAskDocs,
  importSyncClickupDrift,
  importSyncEntityState,
  importSyncInspect,
  importSyncMerge,
  importSyncStatus,
  importSyncTriggerClickup,
} from '@/api/import-sync';
import { isProdEnvironment } from '@/lib/env';
import { resolveMediaUrl } from '@/lib/media';
import { Markdown } from '@/components/shared/Markdown';

// Any diff value that is a media path (`/org/asset/…`) or a URL is rendered as a new-tab link
// (resolveMediaUrl = VITE_MEDIA_HOST + bare path, full URLs pass through) so the operator can
// open the actual before/after media and verify it. Covers storage_path / playback_url /
// poster_url / link and any future media field — keyed on value shape, not field name. Empty
// (∅) and backend-truncated (…) values can't link, so they stay plain.
const isLinkableValue = (v: string): boolean =>
  v !== '∅' && !v.includes('…') &&
  (v.startsWith('/') || v.startsWith('http://') || v.startsWith('https://'));

// Dev-only, super-admin (the /admin layout gates both). Prod writes happen
// server-side via the db-gateway; the browser never holds a prod secret.
export const Route = createFileRoute('/admin/import-sync')({ component: ImportSyncPage });

const CL_JOB = 'courses-lessons';
const ASSET_JOB = 'asset-media';

// A diff row tagged with its entity (assets/courses/lessons), as rendered in the list.
type Row = DiffRow & { entity: string };

function ImportSyncPage() {
  if (isProdEnvironment) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-xl font-bold text-foreground">Import Sync</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This tool runs on the dev environment only — it promotes content dev → prod from there.
        </p>
      </div>
    );
  }
  return <ImportSyncShell />;
}

// Top-level tabs: the operational dev→prod sync (default) and the AI assistant.
function ImportSyncShell() {
  const [topTab, setTopTab] = useState<'sync' | 'upstream' | 'ai'>('sync');
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <h1 className="text-xl font-bold text-foreground">Import Sync — dev → prod</h1>
        <div className="flex gap-1 border-b border-border">
          <TabButton active={topTab === 'sync'} onClick={() => setTopTab('sync')}>
            Operational Sync
          </TabButton>
          <TabButton active={topTab === 'upstream'} onClick={() => setTopTab('upstream')}>
            Upstream check
          </TabButton>
          <TabButton active={topTab === 'ai'} onClick={() => setTopTab('ai')}>
            AI assistance
          </TabButton>
        </div>
        {topTab === 'sync'
          ? <ImportSyncTabs />
          : topTab === 'upstream'
          ? <ClickupDriftTab />
          : <AiAssistanceTab />}
      </div>
    </div>
  );
}

function ImportSyncTabs() {
  // Drives the assets lock + tab badge. NEW courses/lessons must be promoted first
  // (assets link to them by clickup_id). Shares the query cache with the panel (same key).
  const cl = useQuery({
    queryKey: ['import-sync-inspect', CL_JOB],
    queryFn: () => importSyncInspect(CL_JOB),
    refetchOnWindowFocus: false,
  });
  const clEntities = cl.data?.output?.entities ?? [];
  const clNew = clEntities.reduce((n, e) => n + (e.counts.missing_on_prod ?? 0), 0);
  const clChanged = clEntities.reduce((n, e) => n + (e.counts.changed ?? 0), 0);
  const clActionable = clNew + clChanged;
  // Only NEW courses/lessons block assets (a new asset can link to a not-yet-promoted parent).
  // Edited courses/lessons don't affect asset FK resolution, so they don't lock.
  const assetsLocked = clNew > 0;

  const [tab, setTab] = useState<'cl' | 'assets' | null>(null);
  useEffect(() => {
    // Auto-select once on first compare: courses/lessons if anything's actionable, else assets.
    if (tab === null && cl.isSuccess) setTab(clActionable > 0 ? 'cl' : 'assets');
  }, [cl.isSuccess, clActionable, tab]);
  const active = tab ?? 'cl';

  const qc = useQueryClient();
  // Poll the latest content-import run; only while it's actually running.
  const status = useQuery({
    queryKey: ['clickup-sync-status'],
    queryFn: importSyncStatus,
    refetchInterval: (q) => (q.state.data?.run?.status === 'in_progress' ? 4000 : false),
    refetchOnWindowFocus: false,
  });
  const run = status.data?.run ?? null;
  const sync = useMutation({
    mutationFn: importSyncTriggerClickup,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clickup-sync-status'] }),
  });
  // When a ClickUp run finishes, refresh both comparisons so new/edited content shows up.
  const prevStatus = useRef<string | null>(null);
  useEffect(() => {
    if (prevStatus.current === 'in_progress' && run?.status === 'completed') {
      qc.invalidateQueries({ queryKey: ['import-sync-inspect', CL_JOB] });
      qc.invalidateQueries({ queryKey: ['import-sync-inspect', ASSET_JOB] });
    }
    prevStatus.current = run?.status ?? null;
  }, [run?.status, qc]);

  return (
    <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">
              Promote courses &amp; lessons first, then assets (assets link to them).
            </p>
          </div>
          <button
            onClick={() => !sync.isPending && sync.mutate()}
            disabled={sync.isPending}
            title="Pull latest content from ClickUp into dev (runs the content-import pipeline)"
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50"
          >
            {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Run ClickUp sync
          </button>
        </div>

        {(sync.isPending || run) && (
          <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs">
            {sync.isPending
              ? (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> starting…
                </span>
              )
              : run?.status === 'in_progress'
              ? (
                <span className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Loader2 className="h-3 w-3 animate-spin" /> ClickUp sync running{run.current_task
                    ? ` — ${run.current_task}`
                    : ''}…
                </span>
              )
              : run?.status === 'completed'
              ? <span className="text-emerald-600 dark:text-emerald-400">✓ Last ClickUp sync completed</span>
              : run?.status === 'failed'
              ? (
                <span className="text-destructive">
                  ✗ Last ClickUp sync failed{run.error_message ? `: ${run.error_message}` : ''}
                </span>
              )
              : run?.status === 'cancelled'
              ? <span className="text-muted-foreground">Last ClickUp sync was cancelled</span>
              : null}
          </div>
        )}
        {sync.isError && <p className="text-xs text-destructive">{(sync.error as Error).message}</p>}
        {sync.data && !sync.data.ok && <p className="text-xs text-destructive">{sync.data.error}</p>}

        <div className="flex gap-1 border-b border-border">
          <TabButton active={active === 'cl'} onClick={() => setTab('cl')}>
            Courses &amp; Lessons
            {clActionable > 0 && (
              <span className="ml-2 rounded-full bg-primary/15 px-1.5 text-xs text-primary">{clActionable}</span>
            )}
          </TabButton>
          <TabButton
            active={active === 'assets'}
            disabled={assetsLocked}
            title={assetsLocked ? 'Promote new courses & lessons first' : undefined}
            onClick={() => !assetsLocked && setTab('assets')}
          >
            Assets {assetsLocked && <Lock className="ml-1 inline h-3 w-3" />}
          </TabButton>
        </div>

        {assetsLocked && active === 'cl' && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Assets are locked until the {clNew} new course/lesson{clNew === 1 ? '' : 's'} {clNew === 1 ? 'is' : 'are'}
            {' '}promoted — assets may link to them.
          </p>
        )}

        {active === 'cl' ? <ImportSyncJobPanel job={CL_JOB} /> : <ImportSyncJobPanel job={ASSET_JOB} />}
    </div>
  );
}

function TabButton(
  { active, disabled, title, onClick, children }: {
    active: boolean;
    disabled?: boolean;
    title?: string;
    onClick: () => void;
    children: React.ReactNode;
  },
) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      {children}
    </button>
  );
}

// Per-job panel: auto-compares dev↔prod on load (new + edited), lists every difference with the
// exact field/value change, lets the operator pick rows, then dry-run / merge to prod.
function ImportSyncJobPanel({ job }: { job: string }) {
  const qc = useQueryClient();
  const inspect = useQuery({
    queryKey: ['import-sync-inspect', job],
    queryFn: () => importSyncInspect(job),
    refetchOnWindowFocus: false,
  });
  const diff = inspect.data?.output;
  const entities = diff?.entities ?? [];

  // Actionable = new on prod + edited (the upsert fixes both). prod_only is shown read-only
  // (removal is explicit in ClickUp, never promoted from a dev-absence).
  const actionable: Row[] = entities.flatMap((e) =>
    e.rows
      .filter((r) => r.category === 'missing_on_prod' || r.category === 'changed')
      .map((r) => ({ ...r, entity: e.entity }))
  );
  const prodOnly: Row[] = entities.flatMap((e) =>
    e.rows.filter((r) => r.category === 'prod_only').map((r) => ({ ...r, entity: e.entity }))
  );
  const totalMatch = entities.reduce((n, e) => n + (e.counts.match ?? 0), 0);
  const readError = entities.find((e) => e.error)?.error;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dryRun, setDryRun] = useState(true);
  // Re-seed the selection (all actionable rows ticked) every time a fresh comparison lands.
  useEffect(() => {
    if (inspect.isSuccess) setSelected(new Set(actionable.map((r) => r.clickup_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspect.dataUpdatedAt, inspect.isSuccess]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const setAll = (on: boolean) =>
    setSelected(on ? new Set(actionable.map((r) => r.clickup_id)) : new Set());

  const merge = useMutation({
    mutationFn: (commit: boolean) => importSyncMerge(job, [...selected], commit),
    onSuccess: (r) => {
      if (r.ok && r.committed) {
        // Re-compare so merged rows flip to "match" and drop off the list.
        qc.invalidateQueries({ queryKey: ['import-sync-inspect', job] });
        qc.invalidateQueries({ queryKey: ['import-sync-inspect', CL_JOB] });
      }
    },
  });

  const onGo = () => {
    if (!selected.size || merge.isPending) return;
    const commit = !dryRun;
    if (commit && !confirm(`Merge ${selected.size} item(s) to PROD? This writes to production.`)) return;
    merge.mutate(commit);
  };

  const busy = inspect.isLoading || inspect.isFetching;

  return (
    <div className="space-y-4">
      {/* Re-check control + summary. The button is disabled until the current comparison finishes. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {inspect.isLoading
            ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Comparing dev ↔ prod…</span>
            : inspect.isError
            ? <span className="text-destructive">{(inspect.error as Error).message}</span>
            : (
              <span>
                <span className="font-medium text-foreground">{actionable.length}</span> to push
                {' '}({entities.reduce((n, e) => n + e.counts.missing_on_prod, 0)} new,{' '}
                {entities.reduce((n, e) => n + e.counts.changed, 0)} edited)
                {prodOnly.length > 0 && <> · {prodOnly.length} only on prod</>}
                {' '}· {totalMatch} already match
              </span>
            )}
        </div>
        <button
          onClick={() => inspect.refetch()}
          disabled={busy}
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          {busy ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      {readError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          Could not read prod for one or more entities: {readError}
        </div>
      )}

      {!inspect.isLoading && actionable.length === 0 && !readError && (
        <p className="text-sm text-muted-foreground">
          Nothing to push — every dev item already matches prod.
        </p>
      )}

      {actionable.length > 0 && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center justify-between border-b border-border pb-2 text-xs text-muted-foreground">
              <span>{selected.size} of {actionable.length} selected</span>
              <span className="flex gap-3">
                <button type="button" className="underline hover:text-foreground" onClick={() => setAll(true)}>
                  Select all
                </button>
                <button type="button" className="underline hover:text-foreground" onClick={() => setAll(false)}>
                  None
                </button>
              </span>
            </div>
            <ul className="max-h-[28rem] space-y-1 overflow-auto">
              {actionable.map((r) => (
                <DiffRowItem
                  key={`${r.entity}:${r.clickup_id}`}
                  row={r}
                  checked={selected.has(r.clickup_id)}
                  onToggle={() => toggle(r.clickup_id)}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {actionable.length > 0 && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run — apply &amp; roll back, nothing written (uncheck to commit)
          </label>
          <button
            onClick={onGo}
            disabled={!selected.size || merge.isPending}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40 ${
              dryRun ? 'bg-secondary text-foreground' : 'bg-primary text-primary-foreground'
            }`}
          >
            {merge.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {merge.isPending
              ? (dryRun ? 'Dry running…' : 'Merging…')
              : (dryRun ? `Dry-run ${selected.size || ''}` : `Merge ${selected.size || ''} to prod`)}
          </button>
        </div>
      )}

      {merge.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {(merge.error as Error).message}
        </div>
      )}
      {merge.data && !merge.data.ok && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {merge.data.error}
        </div>
      )}
      {merge.data?.ok && (
        <Card>
          <CardContent className="space-y-1 p-4 text-sm">
            <div
              className={`flex items-center gap-2 font-medium ${
                merge.data.committed ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
              }`}
            >
              <CheckCircle2 className="h-4 w-4" />
              {merge.data.committed
                ? 'Merged to prod (committed)'
                : 'Dry run — this is what would merge (rolled back, nothing written)'}
            </div>
            {(merge.data.report ?? []).map((r) => {
              const warn = r.metric.includes('unresolved') && Number(r.value) > 0;
              return (
                <div
                  key={r.metric}
                  className={warn ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}
                >
                  {warn ? '⚠ ' : ''}
                  {r.metric}: {r.value}
                </div>
              );
            })}
            {(merge.data.exceptions ?? []).length > 0 && (
              <div className="mt-2 space-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2">
                <div className="font-medium text-amber-700 dark:text-amber-400">
                  ⚠ Media not ready ({merge.data.exceptions!.length})
                </div>
                {merge.data.exceptions!.map((x, i) => (
                  <div
                    key={`${x.clickup_id}-${x.media_kind}-${i}`}
                    className="text-xs text-amber-700 dark:text-amber-300"
                  >
                    {x.entity} “{x.name}” ({x.media_kind}): {x.message}
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => merge.reset()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Clear
            </button>
          </CardContent>
        </Card>
      )}

      {prodOnly.length > 0 && (
        <Card>
          <CardContent className="space-y-1 p-4">
            <p className="text-sm font-medium text-foreground">
              {prodOnly.length} only on prod — needs review
            </p>
            <p className="text-xs text-muted-foreground">
              These exist on prod but not in dev. They are never auto-removed — to retire content, set its
              card to <span className="font-medium">inactive/archived</span> in ClickUp.
            </p>
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {prodOnly.map((r) => (
                <li key={`${r.entity}:${r.clickup_id}`}>
                  <span className="font-medium text-foreground">{r.label}</span> · {r.entity}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// One difference row: checkbox + label + a "new / edited / moved" badge, and for edited rows
// the per-field "prod value → dev value" lines so the operator sees exactly what will change.
function DiffRowItem(
  { row, checked, onToggle }: { row: Row; checked: boolean; onToggle: () => void },
) {
  const isNew = row.category === 'missing_on_prod';
  const moved = !isNew && (row.note ?? '').split(',').includes('course_ck');
  const badge = isNew ? 'new' : moved ? 'moved' : 'edited';
  const badgeClass = isNew
    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
    : moved
    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
    : 'bg-blue-500/15 text-blue-600 dark:text-blue-400';

  return (
    <li className="rounded-lg border border-border px-2 py-1.5 hover:bg-muted/40">
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 flex-shrink-0 accent-primary"
          checked={checked}
          onChange={onToggle}
        />
        <span className="truncate font-medium">{row.label}</span>
        <span className="ml-auto flex-shrink-0 text-xs text-muted-foreground capitalize">{row.entity}</span>
        <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${badgeClass}`}>{badge}</span>
      </label>
      {row.changes && row.changes.length > 0 && (
        <ul className="ml-6 mt-1 space-y-0.5">
          {row.changes.map((c) => (
            <li key={c.field} className="flex flex-wrap items-center gap-1 text-xs">
              <span className="font-medium text-muted-foreground">{c.field}:</span>
              <ValueCell value={c.prod} tone="prod" />
              <span className="text-muted-foreground">→</span>
              <ValueCell value={c.dev} tone="dev" />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// One value in a field diff. Any media path / URL value becomes a link to the resolved CDN URL,
// opening in a new tab so the operator can verify the before/after media.
function ValueCell({ value, tone }: { value: string; tone: 'prod' | 'dev' }) {
  const cls = tone === 'prod'
    ? 'rounded bg-destructive/10 px-1 font-mono text-destructive'
    : 'rounded bg-emerald-500/10 px-1 font-mono text-emerald-600 dark:text-emerald-400';
  const href = isLinkableValue(value) ? resolveMediaUrl(value) : undefined;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={`${cls} underline hover:opacity-80`}>
        {value} ↗
      </a>
    );
  }
  return <span className={cls}>{value}</span>;
}

// ── Upstream check (ClickUp ↔ dev drift) ─────────────────────────────────────
// Read-only concurrent check: surfaces ClickUp edits that haven't reached dev — the hop the dev→prod
// diff can't see (when a change never syncs, dev==prod and the differ shows nothing). No checkboxes,
// no merge; this only reports. Resolve by re-filing the ClickUp task / running the sync.
function ClickupDriftTab() {
  const drift = useQuery({
    queryKey: ['import-sync-clickup-drift'],
    queryFn: importSyncClickupDrift,
    refetchOnWindowFocus: false,
  });
  const rows = drift.data?.output?.rows ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          ClickUp edits that haven’t reached dev yet — these won’t appear in the dev→prod diff until
          they sync. Read-only; fix by re-filing the ClickUp task into the right list, then re-run the
          ClickUp sync.
        </p>
        <button
          onClick={() => drift.refetch()}
          disabled={drift.isFetching}
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {drift.isFetching
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Search className="h-4 w-4" />}
          {drift.isFetching ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      {drift.isError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          Drift check failed: {drift.error instanceof Error ? drift.error.message : 'unknown error'}
        </div>
      )}

      {!drift.isLoading && !drift.isError && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          All dev courses &amp; lessons match ClickUp — nothing stranded upstream.
        </p>
      )}

      {rows.length > 0 && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2 text-xs text-muted-foreground">
              <span>{drift.data?.summary}</span>
              <span>
                <span className="font-mono text-destructive">current dev</span> →{' '}
                <span className="font-mono text-emerald-600 dark:text-emerald-400">ClickUp</span>
              </span>
            </div>
            {/* No inner max-height: render every row in full and let the page scroll, so the
                list can never hide a discrepancy behind an inner scrollbar. */}
            <ul className="space-y-1">
              {rows.map((r) => <DriftRowItem key={`${r.entity}:${r.clickup_id}`} row={r} />)}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DriftRowItem({ row }: { row: DriftRow }) {
  const isAbsent = row.kind === 'absent';
  const inLessons = row.clickupList === 'lessons';
  const badge = isAbsent ? 'not in lists' : inLessons ? 'wrong list' : 'unsynced edit';
  const badgeClass = isAbsent
    ? 'bg-muted text-muted-foreground'
    : inLessons
    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
    : 'bg-blue-500/15 text-blue-600 dark:text-blue-400';
  const hint = isAbsent
    ? 'ClickUp task isn’t in the Courses or Lessons list — moved elsewhere or deleted.'
    : inLessons
    ? 'This course’s task is filed in the Lessons list, so the course sync never picks it up. Move it back to the Courses list, then re-run the sync.'
    : 'Edited in ClickUp but not yet synced to dev — run the ClickUp sync.';

  return (
    <li className="rounded-lg border border-border px-2 py-1.5 hover:bg-muted/40">
      <div className="flex items-center gap-2 text-sm">
        <span className="truncate font-medium">{row.label}</span>
        {row.live && (
          <span className="flex-shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            live
          </span>
        )}
        <span className="ml-auto flex-shrink-0 text-xs capitalize text-muted-foreground">
          {row.entity}
        </span>
        <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${badgeClass}`}>
          {badge}
        </span>
        {row.clickupUrl && (
          <a
            href={row.clickupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 text-muted-foreground hover:text-foreground"
            title="Open in ClickUp"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
      {row.changes && row.changes.length > 0 && (
        <ul className="ml-1 mt-1 space-y-0.5">
          {row.changes.map((c) => (
            <li key={c.field} className="flex flex-wrap items-center gap-1 text-xs">
              <span className="font-medium text-muted-foreground">{c.field}:</span>
              <span className="rounded bg-destructive/10 px-1 font-mono text-destructive">{c.dev}</span>
              <span className="text-muted-foreground">→</span>
              <span className="rounded bg-emerald-500/10 px-1 font-mono text-emerald-600 dark:text-emerald-400">
                {c.clickup}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="ml-1 mt-1 text-xs text-muted-foreground">{hint}</p>
    </li>
  );
}

// ── AI assistance tab ───────────────────────────────────────────────────────
// Two boxes, both answered by Claude via the edge function: (1) Q&A constrained to the operations
// docs about how the sync works; (2) the live ClickUp↔dev↔prod state of one named item.
function AiAssistanceTab() {
  return (
    <div className="space-y-4">
      <AskDocsBox />
      <EntityStateBox />
    </div>
  );
}

function AskDocsBox() {
  const [q, setQ] = useState('');
  const ask = useMutation({ mutationFn: (question: string) => importSyncAskDocs(question) });
  const submit = () => {
    const t = q.trim();
    if (t && !ask.isPending) ask.mutate(t);
  };
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="text-sm font-medium text-foreground">Ask about the sync</div>
        <p className="text-xs text-muted-foreground">
          How the ClickUp → dev → prod content sync works — answered only from the operations docs.
        </p>
        <textarea
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
          rows={2}
          placeholder="e.g. How do I retire a course? When does the ClickUp sync run?"
          className="w-full rounded-lg border border-border bg-background p-2 text-sm"
        />
        <button
          onClick={submit}
          disabled={!q.trim() || ask.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {ask.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Ask
        </button>
        {ask.isError && <p className="text-sm text-destructive">{(ask.error as Error).message}</p>}
        {ask.data && <Markdown className="rounded-lg bg-muted/40 p-3">{ask.data.answer}</Markdown>}
      </CardContent>
    </Card>
  );
}

function EntityStateBox() {
  const [type, setType] = useState<EntityType>('asset');
  const [query, setQuery] = useState('');
  const check = useMutation({
    mutationFn: (v: { type: EntityType; query: string }) => importSyncEntityState(v.type, v.query),
  });
  const submit = () => {
    const t = query.trim();
    if (t && !check.isPending) check.mutate({ type, query: t });
  };
  const d = check.data?.data;
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="text-sm font-medium text-foreground">Check an item's state</div>
        <p className="text-xs text-muted-foreground">
          Is a specific asset, course or lesson in line with ClickUp and promoted to prod?
        </p>
        <div className="flex flex-wrap gap-2">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as EntityType)}
            className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="asset">Asset</option>
            <option value="course">Course</option>
            <option value="lesson">Lesson</option>
          </select>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder="name or clickup id"
            className="min-w-[12rem] flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
          />
          <button
            onClick={submit}
            disabled={!query.trim() || check.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {check.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Check
          </button>
        </div>
        {check.isError && <p className="text-sm text-destructive">{(check.error as Error).message}</p>}
        {check.data && (
          <div className="space-y-2">
            <Markdown className="rounded-lg bg-muted/40 p-3">{check.data.answer}</Markdown>
            {d && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <StateCol
                  label="ClickUp"
                  name={d.clickup?.name}
                  status={d.clickup?.status ?? undefined}
                  when={d.clickup?.date_updated ?? undefined}
                  error={d.clickup?.error}
                />
                <StateCol label="Dev" name={d.dev?.name} status={d.dev?.status} when={d.dev?.updated_at} />
                <StateCol
                  label="Prod"
                  name={d.prod?.name}
                  status={d.prod?.status}
                  when={d.prod?.updated_at}
                  missing={!d.prod}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StateCol(
  { label, name, status, when, error, missing }: {
    label: string;
    name?: string;
    status?: string;
    when?: string;
    error?: string;
    missing?: boolean;
  },
) {
  return (
    <div className="rounded-lg border border-border p-2">
      <div className="font-medium text-foreground">{label}</div>
      {error
        ? <div className="text-destructive">unreachable</div>
        : missing
        ? <div className="text-muted-foreground">not present</div>
        : (
          <>
            <div className="truncate text-muted-foreground" title={name}>{name ?? '—'}</div>
            {status && <div className="text-muted-foreground">status: {status}</div>}
            {when && <div className="text-muted-foreground">{formatDateNumeric(when)}</div>}
          </>
        )}
    </div>
  );
}
